import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../services/api';
import { PageHeader } from './PageHeader';
import './ExpertChatPage.css';

interface Message {
  id: string;
  sender: 'user' | 'expert';
  text: string;
  created_at: string;
}

interface ExpertInfo {
  id: string;
  name: string;
  role: string;
  photo_url: string | null;
  is_online: boolean;
  rating: number;
  reviews_count: number;
}

const QUICK_TOPICS = [
  'Личный план медитаций',
  'Техники при тревоге',
  'Помощь со сном',
  'Стресс на работе',
];

export const ExpertChatPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: expertId } = useParams<{ id: string }>();

  const [expert, setExpert] = useState<ExpertInfo | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expertId) return;
    apiClient.getExpertChat(expertId)
      .then((res) => {
        setExpert(res.data.expert);
        setMessages(res.data.messages ?? []);
      })
      .catch((e) => console.error('Failed to load chat', e))
      .finally(() => setLoading(false));
  }, [expertId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (msgText: string) => {
    if (!expertId) return;
    const trimmed = msgText.trim();
    if (!trimmed || sending) return;

    setSending(true);
    setText('');

    try {
      const res = await apiClient.sendExpertMessage(expertId, trimmed);
      // Сообщение пользователя показываем сразу.
      setMessages((prev) => [...prev, res.data.user_message]);
      // Ответ эксперта раскрываем «живыми» пузырями: индикатор «печатает…»
      // держится снизу, каждая часть всплывает после своей паузы.
      const parts: Array<Message & { typing_ms?: number }> =
        res.data.expert_messages ?? (res.data.expert_message ? [res.data.expert_message] : []);
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        // Первую часть не тянем (генерация уже заняла время), дальше — по typing_ms.
        const delay = i === 0 ? Math.min(part.typing_ms ?? 900, 1200) : (part.typing_ms ?? 1500);
        await new Promise((r) => setTimeout(r, delay));
        setMessages((prev) => [...prev, part]);
      }
    } catch (e) {
      console.error('Failed to send', e);
      alert('Не удалось отправить сообщение. Попробуйте ещё раз.');
      setText(trimmed);
    } finally {
      setSending(false);
    }
  };

  const handleQuickTopic = (topic: string) => {
    handleSend(topic);
  };

  if (loading) return <div className="cbt-loading">Загружаем чат…</div>;
  if (!expert) return <div className="cbt-loading">Эксперт не найден</div>;

  const showQuickTopics = messages.length <= 1; // только welcome от эксперта

  return (
    <div className="cbt-chat-page">
      <PageHeader title="" onBack={() => navigate('/experts')} />

      <div className="cbt-chat-header">
        <div className="cbt-chat-expert-photo">
          {expert.photo_url
            ? <img src={expert.photo_url} alt={expert.name} />
            : <div className="cbt-chat-expert-placeholder">{expert.name.charAt(0)}</div>
          }
          <span className={`cbt-chat-online-dot ${expert.is_online ? 'cbt-online' : 'cbt-offline'}`} />
        </div>
        <div className="cbt-chat-expert-info">
          <div className="cbt-chat-expert-name">{expert.name}</div>
          <div className="cbt-chat-expert-meta">
            <span className={expert.is_online ? 'cbt-online' : 'cbt-offline'}>
              {expert.is_online ? 'Онлайн' : 'Не в сети'}
            </span>
            <span>·</span>
            <span>★ {expert.rating.toFixed(1)} ({expert.reviews_count})</span>
          </div>
        </div>
      </div>

      <div className="cbt-chat-messages">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`cbt-chat-message ${m.sender === 'user' ? 'cbt-message-user' : 'cbt-message-expert'}`}
          >
            <div className="cbt-chat-bubble">{m.text}</div>
          </div>
        ))}
        {sending && (
          <div className="cbt-chat-message cbt-message-expert">
            <div className="cbt-chat-bubble cbt-chat-typing">
              <span>•</span><span>•</span><span>•</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {showQuickTopics && (
        <div className="cbt-chat-quick-topics">
          {QUICK_TOPICS.map((topic) => (
            <button
              key={topic}
              className="cbt-chat-quick-topic"
              onClick={() => handleQuickTopic(topic)}
              disabled={sending}
            >
              {topic}
            </button>
          ))}
        </div>
      )}

      <div className="cbt-chat-input">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Напишите сообщение…"
          rows={1}
          disabled={sending}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(text);
            }
          }}
        />
        <button
          className="cbt-chat-send"
          onClick={() => handleSend(text)}
          disabled={!text.trim() || sending}
        >
          ➤
        </button>
      </div>
    </div>
  );
};
